import { Button, Modal } from "antd";
import ReadOnlyFormBuilder from "components/molecules/ReadOnlyFormBuilder";
import DataObjectUnobfuscationLoginModal from "components/organisms/DataObjectUnobfuscationLoginModal";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { clearUnmaskedData } from "store/dataObjects/actions";

const INACTIVITY_TIMEOUT_MS = 60000; // 60 seconds

const DataObjectUnobfuscation = ({ schemaFields, selectedDataObject, onSave, readOnly }) => {
    const [isLoginModalVisible, setIsLoginModalVisible] = useState(false);
    const [isDataModalVisible, setIsDataModalVisible] = useState(false);
    const dispatch = useDispatch();
    const inactivityTimerRef = useRef(null);
    const isDataModalVisibleRef = useRef(isDataModalVisible);

    useEffect(() => {
        isDataModalVisibleRef.current = isDataModalVisible;
    }, [isDataModalVisible]);

    const clearInactivityTimer = useCallback(() => {
        if (inactivityTimerRef.current) {
            clearTimeout(inactivityTimerRef.current);
            inactivityTimerRef.current = null;
        }
    }, []);

    const closeDataModal = useCallback(() => {
        setIsDataModalVisible(false);
        dispatch(clearUnmaskedData());
    }, [dispatch]);

    const startInactivityTimer = useCallback(() => {
        clearInactivityTimer();
        inactivityTimerRef.current = setTimeout(() => {
            if (isDataModalVisibleRef.current) {
                closeDataModal();
            }
        }, INACTIVITY_TIMEOUT_MS);
    }, [clearInactivityTimer, closeDataModal]);

    useEffect(() => {
        if (!isDataModalVisible) {
            return;
        }

        startInactivityTimer();

        const resetInactivityTimer = () => {
            startInactivityTimer();
        };

        const listenerOptions = { passive: true };
        document.addEventListener("mousemove", resetInactivityTimer, listenerOptions);
        document.addEventListener("click", resetInactivityTimer, listenerOptions);
        document.addEventListener("keydown", resetInactivityTimer, listenerOptions);

        return () => {
            document.removeEventListener("mousemove", resetInactivityTimer);
            document.removeEventListener("click", resetInactivityTimer);
            document.removeEventListener("keydown", resetInactivityTimer);
            clearInactivityTimer();
        };
    }, [isDataModalVisible, startInactivityTimer, clearInactivityTimer]);

    const handleLoginSuccess = useCallback(() => {
        setIsLoginModalVisible(false);
        setIsDataModalVisible(true);
    }, []);

    const handleOpenLoginModal = useCallback(() => {
        setIsLoginModalVisible(true);
    }, []);

    const handleCloseLoginModal = useCallback(() => {
        setIsLoginModalVisible(false);
    }, []);

    const handleSave = useCallback(
        (updatedPayload) => {
            if (!onSave || !updatedPayload) {
                return;
            }

            const payload = {
                ...updatedPayload,
                version: updatedPayload.version ?? selectedDataObject?.version,
            };
            onSave(payload);
            closeDataModal();
        },
        [onSave, selectedDataObject?.version, closeDataModal],
    );

    const isModalOpen = useMemo(() => isDataModalVisible && !!selectedDataObject, [isDataModalVisible, selectedDataObject]);

    return (
        <>
            <Button onClick={handleOpenLoginModal}>Reveal</Button>

            {isLoginModalVisible && (
                <DataObjectUnobfuscationLoginModal
                    open={isLoginModalVisible}
                    onCancel={handleCloseLoginModal}
                    onSuccess={handleLoginSuccess}
                />
            )}

            <Modal
                open={isModalOpen}
                title="Data Record Preview"
                onCancel={closeDataModal}
                cancelText="Close"
                centered
                destroyOnHidden
                width="50%"
                footer={null}
            >
                {selectedDataObject && (
                    <ReadOnlyFormBuilder
                        selectedDataObject={selectedDataObject}
                        schemaFields={schemaFields}
                        onSave={handleSave}
                        readOnly={readOnly}
                    />
                )}
            </Modal>
        </>
    );
};

export default React.memo(DataObjectUnobfuscation);
